<template>
  <div class="other-events">
    <h3 class="section-title">Upcoming Birthdays</h3>
    <div v-if="loading" class="loading">
      Loading birthdays...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    <div v-else-if="birthdays.length" class="events-list">
      <div class="events-grid">
        <div v-for="birthday in birthdays" 
             :key="birthday.id" 
             class="event-item">
          <div class="member-details">
            <div class="avatar" v-if="birthday.photo_url">
              <img :src="birthday.photo_url" :alt="birthday.first_name">
            </div>
            <div v-else class="avatar initials">
              {{ getInitials(birthday.first_name, birthday.last_name) }}
            </div>
            <div class="name-and-date">
              <span class="name">{{ birthday.first_name }} {{ birthday.last_name }}</span>
              <div class="date-group">
                <span class="weekday">{{ formatWeekday(birthday.next_birthday) }}</span>
                <span class="date">{{ formatDate(birthday.next_birthday) }}</span>
              </div>
            </div>
            <span class="days" :class="{ 'past-birthday': isPastBirthday(birthday.next_birthday) }">{{ getDaysUntil(birthday.next_birthday) }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="no-events">
      No birthdays in the next 30 days
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { userService } from '@/services/api';
import { format, differenceInDays, parseISO, startOfDay } from 'date-fns';

export default {
  name: 'UpcomingBirthdays',
  setup() {
    const birthdays = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const loadBirthdays = async () => {
      loading.value = true;
      try {
        birthdays.value = await userService.getUpcomingBirthdays();
      } catch (error) {
        console.error('Failed to load birthdays:', error);
        error.value = 'Failed to load birthdays';
      } finally {
        loading.value = false;
      }
    };

    const getInitials = (firstName, lastName) => {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    };

    const getDaysUntil = (date) => {
      // Parse the ISO string and get start of day to avoid timezone issues
      const targetDate = startOfDay(parseISO(date));
      const today = startOfDay(new Date());
      
      const days = differenceInDays(targetDate, today);
      if (days === 0) return 'Today!';
      if (days === 1) return 'Tomorrow';
      if (days > 0) return `in ${days} days`;
      if (days === -1) return 'Yesterday';
      return `${Math.abs(days)} days ago`;
    };

    const isPastBirthday = (date) => {
      const targetDate = startOfDay(parseISO(date));
      const today = startOfDay(new Date());
      return differenceInDays(targetDate, today) < 0;
    };

    const formatWeekday = (date) => {
      return format(parseISO(date), 'EEE');
    };

    const formatDate = (date) => {
      return format(parseISO(date), 'MMM d');
    };

    onMounted(loadBirthdays);

    return {
      birthdays,
      getDaysUntil,
      formatDate,
      formatWeekday,
      getInitials,
      isPastBirthday,
      loading,
      error
    };
  }
};
</script>

<style scoped>
.other-events {
  background: white;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.events-list {
  margin-top: 1rem;
}

.events-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 768px) {
  .events-grid {
    grid-template-columns: 1fr;
  }

  .member-details {
    width: 100%;
  }
}

.event-item {
  padding: 1rem;
  border: 1px solid #eee;
  border-radius: 8px;
  transition: background-color 0.2s;
  min-height: 72px;
  display: flex;
  align-items: center;
}

.event-item:hover {
  background-color: #f8f9fa;
  border-color: #e0e0e0;
}

.member-details {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  width: 100%;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f5;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar.initials {
  background: #e3f2fd;
  color: #2196f3;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 1.1rem;
}

.name-and-date {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.name {
  font-weight: 500;
  color: #333;
  display: block;
  margin-bottom: 0.25rem;
  line-height: 1.2;
  font-size: 0.95rem;
}

.date-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.2;
}

.weekday {
  font-size: 0.9rem;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

.date {
  font-size: 0.9rem;
}

.days {
  color: #4285f4;
  font-size: 0.85em;
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-left: auto;
  white-space: nowrap;
}

.days.past-birthday {
  color: #9e9e9e;
  font-style: italic;
}

.no-events {
  color: #666;
  text-align: center;
  padding: 1rem 0;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
}
</style> 